import React, { useMemo } from 'react';
import { Box, Typography, InputBase, Button, Chip } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import CheckIcon from '@mui/icons-material/Check';
import NotificationsActiveOutlinedIcon from '@mui/icons-material/NotificationsActiveOutlined';
import ForwardToInboxOutlinedIcon from '@mui/icons-material/ForwardToInboxOutlined';
import FolderSpecialOutlinedIcon from '@mui/icons-material/FolderSpecialOutlined';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useAuth } from '../auth/AuthProvider';
import { useCases } from '../hooks/useCases';
import { colors, typography, shadows, gradient } from '../theme/designTheme';

// ── Small visual primitives ───────────────────────────────────────────

const Mono = ({ children, sx }) => (
  <Box component="span" sx={{ fontFamily: typography.fontFamilyMono, fontFeatureSettings: '"tnum" 1', ...sx }}>
    {children}
  </Box>
);

const Badge = ({ level, children }) => {
  const base = {
    fontFamily: typography.fontFamily,
    fontSize: '10px', fontWeight: 700, letterSpacing: '0.1em', textTransform: 'uppercase',
    px: 1, py: 0.5, borderRadius: '6px',
    display: 'inline-flex', alignItems: 'center',
  };
  if (level === 'Critical') return (
    <Box sx={{ ...base, bgcolor: colors.critical, color: '#fff', boxShadow: '0 2px 8px rgba(187,27,33,0.25)' }}>{children || 'Critical'}</Box>
  );
  if (level === 'High') return (
    <Box sx={{ ...base, bgcolor: colors.warnBg, color: colors.warn }}>{children || 'High'}</Box>
  );
  if (level === 'Medium') return (
    <Box sx={{ ...base, bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer }}>{children || 'Medium'}</Box>
  );
  return (
    <Box sx={{ ...base, bgcolor: colors.successContainer, color: colors.success }}>{children || 'Low'}</Box>
  );
};

const Kpi = ({ kicker, value, footLeft, footRight, accentColor = colors.primary, valueColor }) => (
  <Box sx={{
    bgcolor: colors.surface, borderRadius: '16px', p: '18px 20px 16px',
    boxShadow: shadows.ambient, position: 'relative', overflow: 'hidden',
  }}>
    <Box sx={{
      position: 'absolute', top: 0, left: 0, right: 0, height: 2,
      bgcolor: accentColor, opacity: 0.9,
    }} />
    <Typography sx={{
      fontSize: '10.5px', letterSpacing: '0.12em', textTransform: 'uppercase',
      color: colors.textSecondary, fontWeight: 600, mb: 1,
    }}>
      {kicker}
    </Typography>
    <Typography sx={{
      fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '30px',
      letterSpacing: '-0.02em', lineHeight: 1, color: valueColor || colors.textPrimary,
    }}>
      {value}
    </Typography>
    <Box sx={{
      display: 'flex', justifyContent: 'space-between', alignItems: 'center',
      mt: 1.75, fontSize: '11.5px', color: colors.textSecondary,
    }}>
      <Box component="span">{footLeft}</Box>
      {footRight}
    </Box>
  </Box>
);

// ── High priority hero card ───────────────────────────────────────────

const PriorityCard = ({ caseData, onClick, variant = 'high' }) => {
  const critical = variant === 'crit';
  const accent = critical ? colors.critical : colors.warn;
  const halo = critical ? 'rgba(187,27,33,0.06)' : 'rgba(139,94,14,0.06)';
  const days = caseData.days_left;
  const overdue = days != null && days < 0;

  return (
    <Box
      onClick={onClick}
      sx={{
        bgcolor: colors.surface, borderRadius: '16px', p: '20px 22px 18px',
        boxShadow: shadows.ambient, position: 'relative', overflow: 'hidden',
        cursor: 'pointer', transition: 'transform 0.2s, box-shadow 0.2s',
        '&:hover': { transform: 'translateY(-2px)', boxShadow: shadows.lift },
      }}
    >
      <Box sx={{ position: 'absolute', top: 0, bottom: 0, left: 0, width: 3, bgcolor: accent }} />
      <Box sx={{
        position: 'absolute', inset: 0, pointerEvents: 'none',
        background: `radial-gradient(360px 160px at 100% 0%, ${halo}, transparent 60%)`,
      }} />

      <Box sx={{ position: 'relative', display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5 }}>
        <Typography sx={{
          fontFamily: typography.fontFamilyMono, fontSize: '11px', fontWeight: 700,
          letterSpacing: '0.1em', color: colors.primary, textTransform: 'uppercase',
        }}>
          REF-{caseData.case_number}
        </Typography>
        <Badge level={caseData.risk_level} />
      </Box>

      <Typography sx={{
        fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '18px',
        letterSpacing: '-0.01em', color: colors.textPrimary, mb: 1,
        position: 'relative',
      }}>
        {caseData.customer_name || caseData.customer_id}
      </Typography>
      <Typography sx={{
        fontSize: '12.5px', color: colors.textSecondary, lineHeight: 1.5, mb: 2.25,
        display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden',
        position: 'relative',
      }}>
        {caseData.ai_summary || 'No AI summary available yet.'}
      </Typography>

      <Box sx={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        pt: 1.75, borderTop: `1px solid ${colors.canvas}`, fontSize: '12px',
        color: colors.textSecondary, position: 'relative',
      }}>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 500, color: overdue ? colors.critical : 'inherit' }}>
          {overdue ? <ErrorOutlineIcon sx={{ fontSize: 13 }} /> : <AccessTimeIcon sx={{ fontSize: 13 }} />}
          <Mono sx={{ fontWeight: 700, color: overdue ? colors.critical : accent }}>
            {days == null ? '—' : overdue ? `${Math.abs(days)}d overdue` : `${days}d left`}
          </Mono>
        </Box>
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75, fontWeight: 500 }}>
          <PersonOutlineIcon sx={{ fontSize: 13 }} />
          <Box component="span" sx={{ textTransform: 'capitalize' }}>{caseData.status || 'Open'}</Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Ongoing case row ──────────────────────────────────────────────────

const AlertSourceBadge = ({ source }) => {
  const isModel = source.type === 'model';
  const fg = isModel ? colors.accent : colors.medium;
  const bg = isModel ? colors.accentDim : colors.mediumBg;
  const border = isModel ? colors.border : colors.mediumBorder;
  return (
    <Chip
      size="small"
      label={`${isModel ? '⚙' : '👤'} ${source.name}`}
      sx={{
        bgcolor: bg, color: fg, border: `1px solid ${border}`, borderRadius: '4px',
        fontFamily: typography.fontFamily, fontSize: '10px', fontWeight: 600, height: 20, mr: 0.5, mb: 0.5,
        '& .MuiChip-label': { px: 0.75 },
      }}
    />
  );
};

const CaseRow = ({ caseData, colorIdx, onClick }) => {
  const level = caseData.risk_level;
  const accent = level === 'Critical' ? colors.critical
    : level === 'High' ? colors.warn
    : level === 'Low' ? colors.success : colors.primary;
  const days = caseData.days_left;
  const overdue = days != null && days < 0;
  const slaColor = overdue ? colors.critical : (days != null && days <= 3 ? colors.warn : colors.textPrimary);
  const slaNum = days == null ? '—' : String(overdue ? Math.abs(days) : days).padStart(2, '0');
  const slaLbl = days == null ? '' : overdue ? 'Days overdue' : 'Days left';

  const initials = (caseData.customer_name || caseData.customer_id || '??')
    .split(' ').map((p) => p[0]).filter(Boolean).join('').slice(0, 2).toUpperCase();

  const statePill = (caseData.status || 'open').toLowerCase();
  const stateIsProgress = statePill.includes('progress');

  return (
    <Box
      onClick={onClick}
      sx={{
        display: 'grid',
        gridTemplateColumns: '44px 220px 88px 1fr 92px 20px',
        gap: 2, alignItems: 'center',
        p: '14px 24px',
        borderLeft: `3px solid ${accent}`,
        cursor: 'pointer', transition: 'background 0.12s',
        '&:hover': { bgcolor: colors.canvas },
        '& + &': { borderTop: `1px solid ${colors.canvas}` },
      }}
    >
      <Box sx={{
        width: 36, height: 36, borderRadius: '50%',
        background: colors.avatar[colorIdx % colors.avatar.length],
        color: '#fff', display: 'grid', placeItems: 'center',
        fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '12px',
        letterSpacing: '0.02em',
      }}>
        {initials}
      </Box>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Mono sx={{ fontSize: '11.5px', fontWeight: 600, color: colors.primary }}>
            REF-{caseData.case_number}
          </Mono>
          <Box sx={{
            fontSize: '10px', letterSpacing: '0.1em', textTransform: 'uppercase',
            fontWeight: 600, px: 0.75, py: 0.25, borderRadius: '4px',
            color: stateIsProgress ? colors.primary : colors.textSecondary,
            bgcolor: stateIsProgress ? colors.primaryContainer : colors.canvas,
          }}>
            {caseData.status || 'Open'}
          </Box>
        </Box>
        <Typography sx={{
          fontSize: '13.5px', fontWeight: 600, color: colors.textPrimary,
          letterSpacing: '-0.005em', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
        }}>
          {caseData.customer_name || caseData.customer_id}
        </Typography>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
        <Badge level={level} />
      </Box>

      <Box sx={{ display: 'flex', flexWrap: 'wrap', pr: 1.5 }}>
        {Array.isArray(caseData.alert_sources) && caseData.alert_sources.length > 0
          ? caseData.alert_sources.map((s, i) => <AlertSourceBadge key={i} source={s} />)
          : <Typography sx={{ fontSize: '12px', color: colors.textSecondary }}>—</Typography>}
      </Box>

      <Box sx={{ textAlign: 'right', fontFamily: typography.fontFamilyDisplay }}>
        <Typography sx={{
          fontWeight: 800, fontSize: '20px', letterSpacing: '-0.01em', lineHeight: 1,
          color: slaColor,
        }}>
          {slaNum}
        </Typography>
        {slaLbl && (
          <Typography sx={{
            fontSize: '9.5px', letterSpacing: '0.12em', textTransform: 'uppercase',
            color: colors.textSecondary, fontWeight: 600, mt: 0.5,
          }}>
            {slaLbl}
          </Typography>
        )}
      </Box>

      <Box sx={{ display: 'grid', placeItems: 'center', color: colors.outlineVariant }}>
        <ChevronRightIcon sx={{ fontSize: 16 }} />
      </Box>
    </Box>
  );
};

// ── Right rail cards ──────────────────────────────────────────────────

const AiInsightCard = ({ dueCount, criticalCount }) => (
  <Box sx={{
    background: gradient.insight, color: '#fff',
    borderRadius: '16px', p: '20px 22px', position: 'relative', overflow: 'hidden',
    boxShadow: shadows.ambient,
  }}>
    <Box sx={{
      position: 'absolute', inset: '-30% -30% auto auto',
      width: 220, height: 220, pointerEvents: 'none',
      background: 'radial-gradient(circle, rgba(255,255,255,0.12), transparent 70%)',
    }} />
    <Box sx={{
      fontSize: '10.5px', letterSpacing: '0.14em', textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.7)', fontWeight: 700, mb: 0.75,
      display: 'inline-flex', alignItems: 'center', gap: 0.75,
    }}>
      <AutoAwesomeIcon sx={{
        fontSize: 14,
        animation: 'sparkle-pulse 2s ease-in-out infinite',
        '@keyframes sparkle-pulse': {
          '0%,100%': { opacity: 0.7, transform: 'scale(1)' },
          '50%': { opacity: 1, transform: 'scale(1.15)' },
        },
      }} />
      AI Insight · Today
    </Box>
    <Typography sx={{ fontSize: '13.5px', lineHeight: 1.5, mb: 1.75 }}>
      You have <Box component="mark" sx={{ bgcolor: 'rgba(255,255,255,0.18)', color: '#fff', px: 0.5, borderRadius: '3px' }}>{dueCount} cases</Box> this week.
      {criticalCount > 0 && (<> Among them, <strong>{criticalCount}</strong> {criticalCount === 1 ? 'is' : 'are'} tagged as critical.</>)}
    </Typography>
    <Box sx={{
      display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5,
      pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.15)',
    }}>
      <Box>
        <Typography sx={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', fontWeight: 600, mb: 0.4 }}>
          Due
        </Typography>
        <Typography sx={{ fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '20px', letterSpacing: '-0.01em' }}>
          {dueCount}
        </Typography>
      </Box>
      <Box>
        <Typography sx={{ fontSize: '10px', letterSpacing: '0.12em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.6)', fontWeight: 600, mb: 0.4 }}>
          Critical
        </Typography>
        <Typography sx={{ fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '20px', letterSpacing: '-0.01em' }}>
          {criticalCount}
        </Typography>
      </Box>
    </Box>
  </Box>
);

const ActivityRow = ({ Icon, iconBg, iconColor, children, time }) => (
  <Box sx={{
    display: 'grid', gridTemplateColumns: '28px 1fr auto', gap: 1.25, py: 1.25, alignItems: 'flex-start',
    '& + &': { borderTop: `1px solid ${colors.canvas}` },
  }}>
    <Box sx={{
      width: 26, height: 26, borderRadius: '8px',
      bgcolor: iconBg, color: iconColor,
      display: 'grid', placeItems: 'center', mt: 0.25,
    }}>
      <Icon sx={{ fontSize: 13 }} />
    </Box>
    <Typography sx={{ fontSize: '12.5px', color: colors.textPrimary, lineHeight: 1.45 }}>
      {children}
    </Typography>
    <Mono sx={{ fontSize: '10.5px', color: colors.textSecondary, letterSpacing: '0.04em', whiteSpace: 'nowrap', mt: 0.5 }}>
      {time}
    </Mono>
  </Box>
);

// ── Main dashboard ────────────────────────────────────────────────────

const Home = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const firstName = user?.firstName || (user?.name || 'there').split(' ')[0];

  const { data: casesData, isLoading } = useCases({ limit: 100 });
  const cases = useMemo(() => casesData?.cases ?? [], [casesData]);

  const kpis = useMemo(() => {
    const critical = cases.filter((c) => c.risk_level === 'Critical').length;
    const active = cases.filter((c) => (c.status || '').toLowerCase() !== 'closed').length;
    const due = cases.filter((c) => c.days_left != null && c.days_left <= 7 && c.days_left >= 0).length;
    const overdue = cases.filter((c) => c.days_left != null && c.days_left < 0).length;
    const closed30d = cases.filter((c) => (c.status || '').toLowerCase() === 'closed').length;
    return { critical, active, due, overdue, closed30d };
  }, [cases]);

  const highPriority = useMemo(() => {
    return [...cases]
      .filter((c) => c.risk_level === 'Critical' || c.risk_level === 'High')
      .sort((a, b) => (b.ai_rating || 0) - (a.ai_rating || 0))
      .slice(0, 3);
  }, [cases]);

  const ongoing = useMemo(() => {
    return [...cases].sort((a, b) => {
      const ratingDiff = (b.ai_rating || 0) - (a.ai_rating || 0);
      if (ratingDiff !== 0) return ratingDiff;
      return (a.days_left ?? Infinity) - (b.days_left ?? Infinity);
    });
  }, [cases]);

  return (
    <Box sx={{ p: '24px 32px 40px', display: 'flex', flexDirection: 'column', gap: 3, minWidth: 0 }}>

      {/* ── Greeting + search ─── */}
      <Box sx={{
        display: 'grid', gridTemplateColumns: '1fr auto',
        alignItems: 'flex-end', gap: 3,
      }}>
        <Box>
          <Typography sx={{ fontSize: '12px', color: colors.textSecondary, mb: 0.75 }}>
            Overview
          </Typography>
          <Typography sx={{
            fontFamily: typography.fontFamilyDisplay, fontWeight: 800, fontSize: '28px',
            letterSpacing: '-0.02em', color: colors.textPrimary,
          }}>
            Good afternoon, <Box component="span" sx={{ color: colors.textSecondary, fontWeight: 700 }}>{firstName}</Box>
          </Typography>
          <Box sx={{
            mt: 0.5, fontSize: '13px', color: colors.textSecondary,
            display: 'flex', gap: 1.25, alignItems: 'center',
          }}>
            <Box component="span">
              You have <Box component="strong" sx={{ color: colors.critical, fontWeight: 700 }}>{kpis.due} cases</Box> due this week
            </Box>
            <Box component="span" sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: colors.outlineVariant }} />
            <Box component="span"><Mono sx={{ color: colors.textPrimary }}>{cases.length}</Mono> assigned to you</Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25 }}>
          <Box sx={{
            display: 'flex', alignItems: 'center', gap: 1.25,
            bgcolor: colors.surface, p: '9px 14px', borderRadius: '10px',
            boxShadow: shadows.ambient, width: 340,
          }}>
            <SearchIcon sx={{ fontSize: 15, color: colors.textSecondary }} />
            <InputBase
              placeholder="Search case #, entity, ABN…"
              sx={{
                flex: 1, fontFamily: typography.fontFamily, fontSize: '13px', color: colors.textPrimary,
                '& input::placeholder': { color: colors.textSecondary, opacity: 1 },
              }}
            />
            <Mono sx={{
              fontSize: '11px', color: colors.textSecondary, bgcolor: colors.canvas,
              p: '2px 6px', borderRadius: '4px', letterSpacing: '0.04em',
            }}>
              ⌘K
            </Mono>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon sx={{ fontSize: 14 }} />}
            sx={{
              fontFamily: typography.fontFamily, fontWeight: 600, fontSize: '13px',
              textTransform: 'none', borderRadius: '9px', px: 2, py: 1.1,
              background: gradient.primary, color: '#fff', boxShadow: shadows.button,
              '&:hover': { background: gradient.primary, filter: 'brightness(1.05)' },
            }}
          >
            New Case
          </Button>
        </Box>
      </Box>

      {/* ── KPI strip ─── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 2 }}>
        <Kpi
          kicker="Critical open" value={kpis.critical}
          valueColor={colors.critical} accentColor={colors.critical}
          footLeft="vs last week"
          footRight={<Mono sx={{ fontWeight: 600, color: colors.critical, display: 'inline-flex', alignItems: 'center', gap: 0.4 }}><ExpandLessIcon sx={{ fontSize: 12 }} />+1</Mono>}
        />
        <Kpi
          kicker="Active cases" value={kpis.active} accentColor={colors.primary}
          footLeft="Queue"
          footRight={
            <Box component="svg" viewBox="0 0 70 22" sx={{ width: 70, height: 22 }} preserveAspectRatio="none">
              <polyline fill="none" stroke={colors.primary} strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" points="0,15 10,12 20,14 30,8 40,10 50,5 60,8 70,4" />
            </Box>
          }
        />
        <Kpi
          kicker="Due this week" value={kpis.due} accentColor={colors.warn}
          footLeft={`${kpis.overdue} overdue`}
          footRight={<Box component="span" sx={{ color: colors.warn, fontWeight: 500 }}>● needs review</Box>}
        />
        <Kpi
          kicker="Closed (30d)" value={kpis.closed30d} accentColor={colors.success}
          footLeft="vs last month"
          footRight={<Mono sx={{ fontWeight: 600, color: colors.success, display: 'inline-flex', alignItems: 'center', gap: 0.4 }}><ExpandMoreIcon sx={{ fontSize: 12 }} />+18%</Mono>}
        />
      </Box>

      {/* ── High priority ─── */}
      <Box sx={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 3 }}>
        <Box>
          <Typography sx={{
            fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '20px',
            letterSpacing: '-0.015em', color: colors.textPrimary,
          }}>
            High Priority Cases
          </Typography>
          <Typography sx={{ fontSize: '12.5px', color: colors.textSecondary, mt: 0.5 }}>
            Top three flagged suspicious activities
          </Typography>
        </Box>
        <Button
          onClick={() => navigate('/cases')}
          endIcon={<ArrowForwardIcon sx={{ fontSize: 12 }} />}
          sx={{
            fontFamily: typography.fontFamily, fontSize: '12.5px', fontWeight: 600, color: colors.textPrimary,
            textTransform: 'none', px: 1.75, py: 1.1, borderRadius: '9px',
            bgcolor: colors.surface, boxShadow: shadows.ambient,
            '&:hover': { bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer },
          }}
        >
          View all cases
        </Button>
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 2 }}>
        {(isLoading ? Array.from({ length: 3 }) : highPriority).map((c, i) => (
          c ? (
            <PriorityCard
              key={c.case_number} caseData={c}
              variant={c.risk_level === 'Critical' ? 'crit' : 'high'}
              onClick={() => navigate(`/cases/${c.case_number}`)}
            />
          ) : (
            <Box key={i} sx={{ bgcolor: colors.surface, borderRadius: '16px', height: 190, boxShadow: shadows.ambient }} />
          )
        ))}
      </Box>

      {/* ── Two col: ongoing list + right rail ─── */}
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 360px', gap: 2.5, alignItems: 'start' }}>
        {/* Ongoing */}
        <Box sx={{
          bgcolor: colors.surface, borderRadius: '16px', boxShadow: shadows.ambient,
          overflow: 'hidden', pt: '4px', pb: '6px',
        }}>
          <Box sx={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            p: '18px 24px 14px', borderBottom: `1px solid ${colors.canvas}`,
          }}>
            <Box>
              <Typography sx={{
                fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '15px', letterSpacing: '-0.01em',
              }}>
                My Ongoing Cases
              </Typography>
              <Typography sx={{ fontSize: '12px', color: colors.textSecondary, mt: 0.25 }}>
                Cases assigned to you · {kpis.active} active
              </Typography>
            </Box>
            <Box sx={{ display: 'flex', gap: 0.5, bgcolor: colors.canvas, p: 0.375, borderRadius: '9px' }}>
              {['All', 'Overdue', 'Due soon', 'Open'].map((t, idx) => (
                <Box
                  key={t}
                  sx={{
                    px: 1.5, py: 0.75, fontSize: '11.5px', borderRadius: '7px', cursor: 'pointer',
                    fontWeight: idx === 0 ? 600 : 500,
                    color: idx === 0 ? colors.primary : colors.textSecondary,
                    bgcolor: idx === 0 ? colors.surface : 'transparent',
                    boxShadow: idx === 0 ? '0 1px 3px rgba(43,52,56,0.06)' : 'none',
                    fontFamily: typography.fontFamily,
                  }}
                >
                  {t}
                </Box>
              ))}
            </Box>
          </Box>

          <Box>
            {(ongoing.length ? ongoing : []).map((c, i) => (
              <CaseRow
                key={c.case_number}
                caseData={c}
                colorIdx={i}
                onClick={() => navigate(`/cases/${c.case_number}`)}
              />
            ))}
            {isLoading && !ongoing.length && Array.from({ length: 4 }).map((_, i) => (
              <Box key={i} sx={{ height: 68, bgcolor: colors.canvas, m: 1, borderRadius: 2 }} />
            ))}
          </Box>

          <Box sx={{
            p: '12px 24px', borderTop: `1px solid ${colors.canvas}`,
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            fontSize: '12px', color: colors.textSecondary,
          }}>
            <Box component="span">Showing {ongoing.length} assigned to you</Box>
            <Button
              onClick={() => navigate('/cases')}
              sx={{
                fontFamily: typography.fontFamily, fontSize: '12.5px', fontWeight: 600, color: colors.textPrimary,
                textTransform: 'none', px: 1.75, py: 1, borderRadius: '9px',
                bgcolor: colors.surface, boxShadow: shadows.ambient,
                '&:hover': { bgcolor: colors.primaryContainer, color: colors.onPrimaryContainer },
              }}
            >
              Open full queue →
            </Button>
          </Box>
        </Box>

        {/* Right rail */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, position: 'sticky', top: 24 }}>
          <AiInsightCard dueCount={kpis.due} criticalCount={kpis.critical} />

          <Box sx={{ bgcolor: colors.surface, borderRadius: '16px', p: '20px 22px', boxShadow: shadows.ambient }}>
            <Box sx={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              mb: 1.75, fontFamily: typography.fontFamilyDisplay, fontWeight: 700, fontSize: '14px',
            }}>
              Recent activity
              <Mono sx={{
                fontWeight: 500, fontSize: '10.5px', color: colors.textSecondary, letterSpacing: '0.08em',
              }}>
                LIVE
              </Mono>
            </Box>
            <Box>
              <ActivityRow
                Icon={NotificationsActiveOutlinedIcon}
                iconBg={colors.errorContainer} iconColor={colors.critical}
                time="2m"
              >
                <Box component="strong" sx={{ fontWeight: 600 }}>Model</Box> escalated a case to critical.
              </ActivityRow>
              <ActivityRow
                Icon={ForwardToInboxOutlinedIcon}
                iconBg={colors.primaryContainer} iconColor={colors.primary}
                time="18m"
              >
                <Box component="strong" sx={{ fontWeight: 600 }}>You</Box> added a note to a case.
              </ActivityRow>
              <ActivityRow
                Icon={CheckIcon}
                iconBg={colors.successContainer} iconColor={colors.success}
                time="1h"
              >
                Verification check passed — Director KYC.
              </ActivityRow>
              <ActivityRow
                Icon={AccessTimeIcon}
                iconBg={colors.warnBg} iconColor={colors.warn}
                time="3h"
              >
                Deadline reminder on an open case.
              </ActivityRow>
              <ActivityRow
                Icon={FolderSpecialOutlinedIcon}
                iconBg={colors.primaryContainer} iconColor={colors.primary}
                time="6h"
              >
                New case assigned to you.
              </ActivityRow>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default Home;
